import { TopNavigation, Modal, AppLayout } from "@cloudscape-design/components";
import { signOut } from 'aws-amplify/auth';
import { useUser } from "../context/UserContext";
import { JuhsisAuthenticator } from "../authentication/JuhsisAuthenticator";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { UserSideNavigation } from "./UserSideNavigation";
import { AppRoutes } from "./AppRoutes";
import { ErrorBoundary } from "./ErrorBoundary";
import { FlashbarProvider } from "../context/FlashbarContext";
import { useMediaQuery } from "../hooks/useMediaQuery";

interface NavigationComponentProps {
    showSignIn: boolean;
    setShowSignIn(value: boolean): void;
}

const NavigationComponent: React.FC<NavigationComponentProps> = ({ showSignIn, setShowSignIn }) => {
    const user = useUser();
    const navigate = useNavigate();
    // Cloudscape's standard breakpoint for collapsing the side nav.
    const isNarrow = useMediaQuery('(max-width: 991px)');
    const [navigationOpen, setNavigationOpen] = React.useState<boolean>(true);

    // Default the side nav to closed on narrow viewports (K3) and open on wider ones.
    useEffect(() => {
        setNavigationOpen(!isNarrow);
    }, [isNarrow]);

    const signInAction = () => setShowSignIn(true);
    const signOutAction = () => { signOut(); };

    useEffect(() => {
        if (user.email) {
            setShowSignIn(false);
        }
    }, [user, setShowSignIn]);

    const i18nStrings = {
        searchIconAriaLabel: 'Search',
        searchDismissIconAriaLabel: 'Close search',
        overflowMenuTriggerText: 'More',
        overflowMenuTitleText: 'All',
        overflowMenuBackIconAriaLabel: 'Back',
        overflowMenuDismissIconAriaLabel: 'Close menu',
    };

    // For the user dropdown we want the email to be the primary identifier
    // (A5/B4): the local-part-with-plus prefix that some users sign up with
    // (e.g. "juhsis+juhsis@gmail.com") is meaningless when displayed alone.
    const displayEmail = user.email || 'Guest';

    return (
        <>
            <TopNavigation
                i18nStrings={i18nStrings}
                identity={{
                    href: '/',
                    title: 'Juhsis',
                    onFollow: (event) => {
                        event.preventDefault();
                        navigate('/');
                    }
                }}
                utilities={[
                    // C3: the brand link in identity is sufficient — no separate Home button.
                    // A5/B4: full email as the dropdown header, signed-in users get just "Sign out".
                    {
                        type: 'menu-dropdown',
                        text: displayEmail,
                        description: user.email ? user.name : undefined,
                        iconName: 'user-profile',
                        ariaLabel: user.email ? `Signed in as ${displayEmail}` : 'Account menu',
                        items: user.email
                            ? [{ id: 'signout', text: 'Sign out' }]
                            : [{ id: 'signin', text: 'Sign in / Sign up' }],
                        onItemClick: (e) => {
                            if (e.detail.id === 'signin') signInAction();
                            else if (e.detail.id === 'signout') signOutAction();
                        }
                    },
                ]}
            />
            <Modal
                visible={showSignIn}
                onDismiss={() => setShowSignIn(false)}
                disableContentPaddings={true}
                // B1: an accessible, screen-reader-announced heading for the auth dialog.
                header="Sign in to TradeDigger"
            >
                <JuhsisAuthenticator />
            </Modal>
            <AppLayout
                toolsHide
                navigationHide={!user.email}
                navigationOpen={navigationOpen}
                onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
                navigation={<UserSideNavigation />}
                content={
                    <FlashbarProvider>
                        <ErrorBoundary>
                            <AppRoutes />
                        </ErrorBoundary>
                    </FlashbarProvider>
                }
            />
        </>
    );
};

export default NavigationComponent;
